/**
 * Cart Drawer — Full AJAX cart with Shopify Section Rendering API
 *
 * Provides:
 *   window.openCart()          — Open the cart drawer
 *   window.closeCart()         — Close the cart drawer
 *   window.addToCart(formData) — Add item via AJAX, open drawer, refresh
 *   window.refreshCartDrawer() — Re-render cart drawer via Section Rendering API
 */
(function() {
  'use strict';

  /* ── Currency-aware price formatting ── */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    var formatted;
    if (window.__shopMoney && window.__shopMoney.format) {
      formatted = window.__shopMoney.format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
        .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
    } else {
      formatted = amount + ' \u20AC';
    }
    /* Compact symbol notation: "EUR" \u2192 "\u20AC" so the shipping bar stays single-line
       on mobile. Skipped for currencies where ISO code is the symbol (CHF, PLN). */
    if (window.__shopMoney && window.__shopMoney.iso && window.__shopMoney.symbol
        && window.__shopMoney.iso !== window.__shopMoney.symbol) {
      formatted = formatted.split(window.__shopMoney.iso).join(window.__shopMoney.symbol);
    }
    return formatted;
  }

  /* ── DOM references (may be null on pages without a cart drawer) ── */
  var drawer    = document.getElementById('cart-drawer');
  var backdrop  = document.getElementById('cart-backdrop');

  /* Element that had focus before the drawer opened (restored on close) */
  var lastFocused = null;

  /* Read free-shipping threshold from data attribute (falls back to 50) */
  var SHIPPING_THRESHOLD = drawer
    ? parseFloat(drawer.getAttribute('data-threshold')) || 50
    : 50;

  /* i18n strings for the JS-rendered shipping bar (from #cart-drawer data-attrs; German fallbacks) */
  function _i18n(attr, fb) { var v = drawer ? drawer.getAttribute(attr) : null; return (v != null && v !== '') ? v : fb; }
  var TXT = {
    shipReached:        _i18n('data-i18n-ship-reached', 'Kostenloser Versand!'),
    shipRemaining:      _i18n('data-i18n-ship-remaining', 'Noch <strong>%AMOUNT%</strong> bis zum kostenlosen Versand'),
    shipRemainingShort: _i18n('data-i18n-ship-remaining-short', 'Noch <strong>%AMOUNT%</strong> bis gratis Versand'),
    free:               _i18n('data-i18n-free', 'Kostenlos')
  };

  /* ────────────────────────────────────────────────────────────────── */
  /*  Open / Close                                                      */
  /* ────────────────────────────────────────────────────────────────── */

  function openCart() {
    var d = document.getElementById('cart-drawer');
    var b = document.getElementById('cart-backdrop');
    var wasOpen = d && d.classList.contains('open');
    if (d) d.classList.add('open');
    if (b) b.classList.add('open');
    document.body.classList.add('cart-open');
    /* Move keyboard focus into the drawer; remember the opener for closeCart */
    if (d && !wasOpen) {
      lastFocused = document.activeElement;
      var closeBtn = d.querySelector('.cart-close-btn');
      if (closeBtn) {
        requestAnimationFrame(function() { closeBtn.focus(); });
      }
    }
    updateCartDelivery();
    /* Re-sync drawer with server-side cart state. Covers the common case where
       the user removed/changed a discount code in the checkout and returned —
       the Liquid-rendered drawer would still show the stale discount otherwise. */
    refreshCartDrawer();
  }

  function closeCart() {
    var d = document.getElementById('cart-drawer');
    var b = document.getElementById('cart-backdrop');
    if (d) d.classList.remove('open');
    if (b) b.classList.remove('open');
    document.body.classList.remove('cart-open');
    /* Return focus to the element that opened the drawer */
    if (lastFocused && typeof lastFocused.focus === 'function' && document.contains(lastFocused)) {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Cart count badge                                                  */
  /* ────────────────────────────────────────────────────────────────── */

  function updateCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var badges = document.querySelectorAll('.cart-count');
        badges.forEach(function(el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });

        /* Also update .cart-badge elements (legacy / alternate markup) */
        document.querySelectorAll('.cart-badge').forEach(function(el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function(err) {
        console.error('[cart-drawer] updateCartCount failed:', err);
      });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Section Rendering API — re-fetch and swap cart drawer HTML        */
  /* ────────────────────────────────────────────────────────────────── */

  function refreshCartDrawer() {
    /* Locale-Root (z.B. "/fr/") mitnehmen, sonst liefert der Section-Fetch bei
       Pfad-Präfix-Sprachen (Dev-Server, Subfolder-Markets) immer die Default-Sprache. */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newContent = doc.querySelector('.cart-drawer');
        var currentDrawer = document.querySelector('.cart-drawer');

        if (newContent && currentDrawer) {
          currentDrawer.innerHTML = newContent.innerHTML;
          bindCartEvents();
          startTrustRotation(); /* DOM was replaced — re-init the carousel */
          /* innerHTML swap drops focus to <body>; pull it back into the open drawer */
          if (currentDrawer.classList.contains('open') && !currentDrawer.contains(document.activeElement)) {
            var closeBtn = currentDrawer.querySelector('.cart-close-btn');
            if (closeBtn) closeBtn.focus();
          }
        }
        updateCartCount();
        updateShippingBar();
        updateCartDelivery();
        /* Let other components (e.g. gifts-popup.js) react to the fresh DOM. */
        document.dispatchEvent(new CustomEvent('cart:rerendered'));
      })
      .catch(function(err) {
        console.error('[cart-drawer] refreshCartDrawer failed:', err);
        /* Fallback: at least update the count */
        updateCartCount();
      });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Shipping progress bar + shipping cost in footer                   */
  /* ────────────────────────────────────────────────────────────────── */

  function updateShippingBar() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var totalPrice = cart.total_price / 100; /* Shopify returns cents */
        var remaining  = Math.max(0, SHIPPING_THRESHOLD - totalPrice);
        var progress   = Math.min(100, (totalPrice / SHIPPING_THRESHOLD) * 100);
        var reached    = remaining <= 0;

        /* FREESHIPPING-Kampagnencode: angewendet + applicable → Versand frei
           unabhängig von der Schwelle. Läuft unabhängig vom PDP-Widget-Schalter
           (freeshipping_code_enabled) — ein abgelaufener Code ist nie
           applicable. Sync mit dem Liquid-Erstrender in cart-drawer.liquid. */
        var fsDrawerEl = document.getElementById('cart-drawer');
        var fsCode = fsDrawerEl ? (fsDrawerEl.getAttribute('data-fs-code') || '').trim() : '';
        if (fsCode && (cart.discount_codes || []).some(function(dc) {
          return dc.applicable && String(dc.code).toUpperCase() === fsCode.toUpperCase();
        })) {
          reached = true;
          progress = 100;
        }

        /* Versandkostenfreie Produkte (Gewichtsregel im Shop, z. B. 2% BHA Peeling 30 ml
           der Meta-Landingpage): liegt eines im Warenkorb, ist der Versand frei —
           Handles kommen aus data-fs-free-handles (Sync mit cart-drawer.liquid). */
        var fsFreeHandles = fsDrawerEl ? (fsDrawerEl.getAttribute('data-fs-free-handles') || '').split(',').map(function(h) { return h.trim(); }).filter(Boolean) : [];
        if (fsFreeHandles.length && (cart.items || []).some(function(item) {
          return fsFreeHandles.indexOf(item.handle) !== -1;
        })) {
          reached = true;
          progress = 100;
        }

        var shippingBar  = document.querySelector('[data-shipping-bar]');
        var shippingText = document.querySelector('[data-shipping-text]');
        var shippingFill = document.querySelector('[data-shipping-fill]');

        if (shippingFill) {
          shippingFill.style.width = progress + '%';
        }

        if (shippingText) {
          if (reached) {
            shippingText.innerHTML = '<svg class="cart-shipping-icon cart-shipping-icon--lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><strong>' + TXT.shipReached + '</strong>';
            if (shippingBar) shippingBar.classList.add('reached');
          } else {
            var remainStr = formatMoney(remaining * 100);
            shippingText.innerHTML = '<svg class="cart-shipping-icon cart-shipping-icon--lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg><span class="cart-shipping-msg--long">' + TXT.shipRemaining.replace('%AMOUNT%', remainStr) + '</span><span class="cart-shipping-msg--short">' + TXT.shipRemainingShort.replace('%AMOUNT%', remainStr) + '</span>';
            if (shippingBar) shippingBar.classList.remove('reached');
          }
        }

        /* Update shipping cost in footer */
        var costEl = document.querySelector('[data-shipping-cost-value]');
        if (costEl) {
          if (reached) {
            costEl.innerHTML = '<span class="cart-free-shipping">' + TXT.free + '</span>';
          } else {
            var d = document.getElementById('cart-drawer');
            var costText = d ? d.getAttribute('data-shipping-cost') : formatMoney(495);
            costEl.textContent = costText || formatMoney(495);
          }
        }
      })
      .catch(function(err) {
        console.error('[cart-drawer] updateShippingBar failed:', err);
      });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Delivery estimate in cart drawer                                  */
  /* ────────────────────────────────────────────────────────────────── */

  function updateCartDelivery() {
    var textEl = document.querySelector('[data-cart-delivery-text]');
    if (!textEl) return;

    /* Wait for DeliveryEstimate to be loaded */
    if (!window.DeliveryEstimate || !window.DeliveryEstimate.calculate) {
      textEl.textContent = '';
      return;
    }

    var d = document.getElementById('cart-drawer');
    if (!d) return;

    var DE = window.DeliveryEstimate;
    var cutoff = DE.parseCutoff(d.getAttribute('data-cutoff'));
    var bisRaw = d.getAttribute('data-back-in-stock');
    var cfg = {
      cutoffHour:      cutoff.hour,
      cutoffMinute:    cutoff.minute,
      shippingDays:    parseInt(d.getAttribute('data-shipping-days'), 10) || 1,
      warehouseOffset: parseInt(d.getAttribute('data-warehouse-offset'), 10) || 0,
      holidays:        DE.parseHolidays(d.getAttribute('data-holidays')),
      backInStock:     bisRaw ? new Date(bisRaw) : null
    };

    var now    = new Date();
    var result = DE.calculate(now, cfg);
    var days   = DE.diffDays(now, result.deliveryDate);

    var label;
    if (days <= 1) {
      label = DE.t('cart_delivery_tomorrow');
    } else if (days <= 6) {
      label = DE.t('cart_delivery_weekday', { weekday: DE.weekdayName(result.deliveryDate) });
    } else {
      label = DE.t('cart_delivery_date',    { date:    DE.formatDate(result.deliveryDate) });
    }

    textEl.textContent = label;
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  AJAX Cart operations                                              */
  /* ────────────────────────────────────────────────────────────────── */

  /**
   * Change the quantity of an existing line item.
   * @param {string} key — Line item key (from cart.items[].key)
   * @param {number} quantity — New quantity (0 = remove)
   */
  function updateQuantity(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Quantity change failed');
      return r.json();
    })
    .then(function() {
      refreshCartDrawer();
    })
    .catch(function(err) {
      console.error('[cart-drawer] updateQuantity failed:', err);
    });
  }

  /**
   * Remove a line item from the cart.
   * @param {string} key — Line item key
   */
  function removeItem(key) {
    updateQuantity(key, 0);
  }

  /**
   * Add item(s) to the cart.
   * @param {FormData} formData — Form data from a product form
   * @returns {Promise}
   */
  function addToCart(formData) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: formData
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Add to cart failed');
      return r.json();
    })
    .then(function(item) {
      refreshCartDrawer();
      /* Allow upsell popup to intercept (defined in upsell-popup.js) */
      if (typeof window._onCartAdd === 'function' && !window._suppressUpsell) {
        window._onCartAdd(item);
      } else {
        openCart();
      }
      window._suppressUpsell = false;
      return item;
    })
    .catch(function(err) {
      console.error('[cart-drawer] addToCart failed:', err);
      throw err;
    });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Trust-line carousel — fades between "30-Tage Garantie" and        */
  /*  "1.000.000+ zufriedene Kunden". Saves vertical footer space and   */
  /*  gives the items area more room.                                   */
  /*                                                                    */
  /*  Cycle (per item):                                                 */
  /*    [visible 5s] → [fade-out 700ms] → [blank pause 400ms]           */
  /*                 → [fade-in 700ms next item] → [visible 5s] → …     */
  /*  The blank pause makes the change noticeable — without it the      */
  /*  cross-fade is so subtle that a quick blink misses the swap.       */
  /* ────────────────────────────────────────────────────────────────── */

  var TRUST_VISIBLE_MS = 5000;  /* how long each line stays fully shown  */
  var TRUST_FADE_MS    = 700;   /* must match CSS .cart-trust-item transition */
  var TRUST_GAP_MS     = 400;   /* extra invisible pause between out + in     */
  var trustTimeout = null;

  function startTrustRotation() {
    if (trustTimeout) {
      clearTimeout(trustTimeout);
      trustTimeout = null;
    }
    var items = document.querySelectorAll('[data-cart-trust] .cart-trust-item');
    if (items.length < 2) return;

    /* Reset to first item — Liquid renders is-active on item[0] on first paint,
       but after refreshCartDrawer we re-init the cycle from scratch. */
    var current = 0;
    items.forEach(function(el, i) {
      el.classList.toggle('is-active', i === current);
    });

    function rotate() {
      items[current].classList.remove('is-active'); /* start fade-out */
      /* After fade-out + blank pause, swap and start fade-in on next item */
      trustTimeout = setTimeout(function() {
        current = (current + 1) % items.length;
        items[current].classList.add('is-active'); /* start fade-in */
        /* Once the new item is fully visible, hold for VISIBLE_MS, then go again */
        trustTimeout = setTimeout(rotate, TRUST_FADE_MS + TRUST_VISIBLE_MS);
      }, TRUST_FADE_MS + TRUST_GAP_MS);
    }

    /* First rotation after the initial visible window */
    trustTimeout = setTimeout(rotate, TRUST_VISIBLE_MS);
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Coupon toggle + apply + remove                                    */
  /* ────────────────────────────────────────────────────────────────── */

  function toggleCoupon() {
    var form   = document.querySelector('[data-coupon-form]');
    var toggle = document.querySelector('[data-coupon-toggle]');
    if (form)   form.classList.toggle('open');
    if (toggle) toggle.classList.toggle('open');
    if (form && form.classList.contains('open')) {
      var input = form.querySelector('[data-coupon-input]');
      if (input) setTimeout(function() { input.focus(); }, 200);
    }
  }

  /**
   * Read translated strings from the drawer's data-* attributes.
   * Liquid sets them on the <aside data-coupon-msg-*> element so we don't
   * have to inline locale lookups in JS.
   */
  function couponMsg(key) {
    var d = document.getElementById('cart-drawer');
    return (d && d.getAttribute('data-coupon-msg-' + key)) || '';
  }

  function setCouponMessage(text, kind /* 'success' | 'error' | '' */) {
    var msg = document.querySelector('[data-coupon-message]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.classList.remove('is-success', 'is-error');
    if (kind === 'success') msg.classList.add('is-success');
    if (kind === 'error')   msg.classList.add('is-error');
  }

  /**
   * Apply a discount code to the cart.
   *
   * Uses POST /cart/update.js with a `discount` parameter — undocumented
   * but stable Shopify AJAX endpoint that works on all plans, returns the
   * updated cart as JSON, and bypasses the `/discount/<CODE>` redirect/auth
   * flow that the local Shopify CLI dev server doesn't proxy correctly.
   *
   * Three result states from cart.discount_codes:
   *   1. Code present + applicable=true   → applied, discount is in cart total
   *   2. Code present + applicable=false  → valid code, but conditions not met
   *                                         (e.g. min. order value, restricted products,
   *                                          or empty cart) — kept for checkout
   *   3. Code not in list                 → invalid code (Shopify silently rejects)
   */
  function applyDiscountCode(rawCode) {
    var code = (rawCode || '').trim();
    if (!code) return;

    var submitBtn = document.querySelector('[data-coupon-submit]');
    var input     = document.querySelector('[data-coupon-input]');
    if (submitBtn) submitBtn.classList.add('is-loading');
    if (input)     input.disabled = true;
    setCouponMessage(couponMsg('applying') || 'Wird angewendet…', '');

    /* Der `discount`-Parameter ERSETZT die komplette Discount-Session —
       nur den neuen Code zu senden würde bereits eingelöste Codes rauswerfen
       (Bug 2026-08: NESH20 flog beim Einlösen von FREESHIPPING raus).
       Deshalb: bestehende Codes aus /cart.js holen und als komma-getrennte
       Liste MIT dem neuen Code setzen. Ob Codes kombinierbar sind,
       entscheidet Shopify serverseitig (combinesWith der Discounts) —
       nicht kombinierbare verwirft der Server still. */
    var hadOthers = false;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var codes = (cart.discount_codes || []).map(function(d) { return d.code; });
        hadOthers = codes.length > 0;
        var exists = codes.some(function(c) { return c.toLowerCase() === code.toLowerCase(); });
        if (!exists) codes.push(code);
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        });
      })
    .then(function(r) {
      /* 429 = rate-limited (Shopify protects against brute-forcing codes).
         422 = unprocessable (e.g. malformed code).
         5xx = server error. All non-2xx → distinct user message. */
      if (r.status === 429) {
        setCouponMessage(couponMsg('rate_limited') || 'Zu viele Versuche, bitte kurz warten', 'error');
        return null;
      }
      if (!r.ok) {
        setCouponMessage(couponMsg('error') || 'Fehler beim Einlösen', 'error');
        return null;
      }
      return r.json();
    })
    .then(function(cart) {
      if (!cart) return; /* error-status handler already set the message */

      var entry = (cart.discount_codes || []).find(function(d) {
        return d.code && d.code.toLowerCase() === code.toLowerCase();
      });

      if (entry && entry.applicable) {
        /* State 1: Applied + active — discount is now in cart total */
        var tpl = couponMsg('applied') || 'Code [code] angewendet';
        setCouponMessage(tpl.split('[code]').join(entry.code), 'success');
        if (input) input.value = '';
        return refreshCartDrawer();
      } else if (entry) {
        /* State 2: Valid code but not applicable to the current cart
           (e.g. min. order value not met, restricted products, empty cart).
           Code is kept and will activate at checkout once conditions are met. */
        var tplPending = couponMsg('pending') || 'Code [code] gespeichert — wird im Checkout angewendet';
        setCouponMessage(tplPending.split('[code]').join(entry.code), 'success');
        if (input) input.value = '';
        return refreshCartDrawer();
      } else if (hadOthers) {
        /* State 3a: Neuer Code fehlt in der Liste, obwohl andere Codes aktiv
           sind — entweder ungültig ODER von Shopify als nicht kombinierbar
           verworfen. Beides ist von außen nicht unterscheidbar. */
        var tplConflict = couponMsg('conflict') || 'Code ungültig oder nicht mit den eingelösten Codes kombinierbar';
        setCouponMessage(tplConflict.split('[code]').join(code), 'error');
        return refreshCartDrawer();
      } else {
        /* State 3b: Code not in list = Shopify silently rejected it as invalid */
        setCouponMessage(couponMsg('invalid') || 'Code ungültig', 'error');
      }
    })
    .catch(function(err) {
      console.error('[cart-drawer] applyDiscountCode failed:', err);
      setCouponMessage(couponMsg('error') || 'Fehler beim Einlösen', 'error');
    })
    .then(function() {
      if (submitBtn) submitBtn.classList.remove('is-loading');
      if (input)     input.disabled = false;
    });
  }

  /**
   * Remove an applied discount code.
   *
   * Der `discount`-Parameter setzt die komplette Code-Liste — gezieltes
   * Entfernen heißt also: verbleibende Codes (ohne den geklickten) neu
   * setzen. Leere Liste ('') löscht die Discount-Session komplett.
   */
  function removeDiscountCode(code) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var remaining = (cart.discount_codes || [])
          .map(function(d) { return d.code; })
          .filter(function(c) { return c.toLowerCase() !== (code || '').toLowerCase(); });
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: remaining.join(',') })
        });
      })
    .then(function() { return refreshCartDrawer(); })
    .then(function() {
      var tpl = couponMsg('removed') || 'Code [code] entfernt';
      setCouponMessage(code ? tpl.split('[code]').join(code) : '', '');
    })
    .catch(function(err) {
      console.error('[cart-drawer] removeDiscountCode failed:', err);
      setCouponMessage(couponMsg('error') || 'Fehler beim Entfernen', 'error');
    });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Event binding                                                     */
  /* ────────────────────────────────────────────────────────────────── */

  /**
   * Delegated click handler — lives on the drawer element, only bound once.
   * Works after innerHTML replacement because it delegates to child selectors.
   */
  function handleDrawerClick(e) {
    var target = e.target;

    /* Close button */
    if (target.closest('.cart-close-btn')) {
      e.preventDefault();
      closeCart();
      return;
    }

    /* Quantity buttons: data-qty-action="minus|plus" data-line-key="..." */
    var qtyBtn = target.closest('[data-qty-action]');
    if (qtyBtn) {
      e.preventDefault();
      var action  = qtyBtn.getAttribute('data-qty-action');
      var lineKey = qtyBtn.getAttribute('data-line-key');
      var qtyEl   = qtyBtn.parentNode.querySelector('.cart-qty-value');
      var current = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

      if (action === 'minus') {
        updateQuantity(lineKey, Math.max(0, current - 1));
      } else if (action === 'plus') {
        updateQuantity(lineKey, current + 1);
      }
      return;
    }

    /* Remove button: data-remove-key="..." */
    var removeBtn = target.closest('[data-remove-key]');
    if (removeBtn) {
      e.preventDefault();
      removeItem(removeBtn.getAttribute('data-remove-key'));
      return;
    }

    /* Cross-sell add-to-cart button */
    var addBtn = target.closest('[data-add-to-cart]');
    if (addBtn) {
      e.preventDefault();
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      addBtn.textContent = '\u2713 Hinzugef\u00fcgt';
      addBtn.classList.add('added');
      window._suppressUpsell = true;
      addToCart(formData);
      return;
    }

    /* Coupon toggle */
    if (target.closest('[data-coupon-toggle]')) {
      e.preventDefault();
      toggleCoupon();
      return;
    }

    /* Coupon submit */
    if (target.closest('[data-coupon-submit]')) {
      e.preventDefault();
      var input = document.querySelector('[data-coupon-input]');
      if (input) applyDiscountCode(input.value);
      return;
    }

    /* Coupon chip remove */
    var removeChip = target.closest('[data-coupon-remove]');
    if (removeChip) {
      e.preventDefault();
      removeDiscountCode(removeChip.getAttribute('data-coupon-remove'));
      return;
    }
  }

  /* Enter key in coupon input → submit */
  function handleDrawerKeydown(e) {
    if (e.key !== 'Enter') return;
    if (!e.target.matches('[data-coupon-input]')) return;
    e.preventDefault();
    applyDiscountCode(e.target.value);
  }

  function bindCartEvents() {
    var currentDrawer = document.querySelector('.cart-drawer');
    if (!currentDrawer || currentDrawer._bound) return;
    currentDrawer.addEventListener('click', handleDrawerClick);
    currentDrawer.addEventListener('keydown', handleDrawerKeydown);
    currentDrawer._bound = true;
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Global event listeners                                            */
  /* ────────────────────────────────────────────────────────────────── */

  function initGlobalListeners() {
    /* Cart trigger buttons (header icons etc.) */
    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('.cart-trigger');
      if (trigger) {
        e.preventDefault();
        openCart();
      }
    });

    /* Backdrop click closes drawer */
    if (backdrop) {
      backdrop.addEventListener('click', function() {
        closeCart();
      });
    }

    /* Also handle dynamically rendered backdrops */
    document.addEventListener('click', function(e) {
      if (e.target.id === 'cart-backdrop' || e.target.classList.contains('cart-backdrop')) {
        closeCart();
      }
    });

    /* Escape key closes drawer */
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeCart();
      }
    });

    /* bfcache restore: when the user returns from /checkout via browser-back,
       the page is restored from memory without re-running scripts. Refresh the
       drawer so any discount removed in checkout disappears here too. */
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) {
        refreshCartDrawer();
      }
    });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Initialisation                                                    */
  /* ────────────────────────────────────────────────────────────────── */

  initGlobalListeners();
  bindCartEvents();
  updateCartCount();
  startTrustRotation();

  /* ────────────────────────────────────────────────────────────────── */
  /*  Expose on window for cross-file usage                             */
  /* ────────────────────────────────────────────────────────────────── */

  window.openCart          = openCart;
  window.closeCart         = closeCart;
  window.addToCart         = addToCart;
  window.refreshCartDrawer = refreshCartDrawer;
  window.updateCartCount   = updateCartCount;
  window.toggleCoupon      = toggleCoupon;

})();
